/* eslint-disable @typescript-eslint/no-explicit-any */
import $ from "jquery";
import React, { useEffect, useState } from "react";
import { Form, Pagination, Card, Col, Table } from "react-bootstrap";

import { useDataFilterTable } from "../hooks/useDataFilterTable";
import MobileSortingDiaglog from "./MobileSortingDiaglog";

import IcomoonIcon from "@icons/IcomoonIcon";
import SntSortIcon from "@icons/SntSortIcon";
import SntActionButton from "@/cleanup/components/Buttons/SntActionButton";
import LoaderSpinner from "../../ReactBootstrap/LoaderSpinner/LoaderSpinner";
import styled from "styled-components";
import NoDataContainer from "@/cleanup/containers/NoData/NoDataContainer";

import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

const Row = styled.tbody<{ decoratorColor?: string }>`
  tr:first-child {
    border-top: 2px solid ${(props) => props.decoratorColor || "transparent"};
  }
`;

interface SntDataCardProps {
  dataFilter?: any;
  data?: object[];
  onConfigColumn?: () => void;
  loading?: boolean;
  noDataText?: string;
  isShowSelectPageSize?: boolean;
  isFloatingFooter?: boolean;
  isShowPaging?: boolean;
  isShowDecoratorColor?: boolean;
  isDisabbledGroup?: boolean;
}

export const SntDataCard = ({
  dataFilter,
  data,
  onConfigColumn,
  loading,
  noDataText,
  isShowSelectPageSize = true,
  isFloatingFooter = true,
  isShowPaging = true,
  isShowDecoratorColor = false,
  isDisabbledGroup = true,
}: SntDataCardProps) => {
  
  // Features access handled by hasFeature hook

  if (!noDataText) noDataText = translate(LanguageKey.no_data_key);

  const table = useDataFilterTable(dataFilter || {} as any, data || [], {});
  const {
    headerGroups,
    prepareRow,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    totalItems,
    toggleSortBy,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = table;

  // floating pager
  useEffect(() => {
    if (isFloatingFooter) {
      if ($(".fl-scrolls").length < 1) {
        (($(".table-container") as any) as any).floatingScroll?.();
      }
    }
    return () => {
      (($(".table-container") as any) as any).floatingScroll?.("destroy");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  useEffect(() => {
    if (isFloatingFooter) {
      setTimeout(() => {
        (($(".table-container") as any) as any).floatingScroll?.("update");
        const flScrollsEl = $(".fl-scrolls");
        flScrollsEl.css("margin-bottom", $(".table-footer").height() || "51px");
        $(".table-container").css(
          "margin-bottom",
          Number.parseInt(($(".table-footer").height() || "0") as string) - 27 + "px" || "51px"
        );
      }, 10);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <>
      <>
        {rows.length === 0 && !loading ? (
          <NoData noDataText={noDataText} />
        ) : (
          <div style={{ marginBottom: 86 }}>
            {rows.map((row: any, i: any) => {
              prepareRow(row);
              let decoratorColor: string | null = "";
              if (isShowDecoratorColor) {
                decoratorColor =
                  row.original && row.original.decoratorColor
                    ? row.original.decoratorColor
                    : null;
              }

              return (
                <TableCard
                  key={i}
                  data={row.cells}
                  decoratorColor={decoratorColor || undefined}
                />
              );
            })}
          </div>
        )}
      </>

      <div style={{ marginBottom: "5px" }}>&nbsp;</div>
      {isShowPaging && (
        <div
          className={`table-footer ${
            isFloatingFooter ? "floating-footer" : ""
          }`}
          style={{ left: 12, right: 12 }}
        >
          <div
            style={{
              padding: isFloatingFooter ? "0.5rem" : undefined,
              paddingBottom: isFloatingFooter ? "0px" : undefined,
            }}
          >
            <SntTablePaging
              dataFilter={dataFilter}
              isDisabbledGroup={isDisabbledGroup}
              toggleSortBy={toggleSortBy}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              pageCount={pageCount}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageIndex={pageIndex}
              pageOptions={pageOptions}
              pageSize={pageSize}
              setPageSize={setPageSize}
              onConfigColumn={onConfigColumn}
              isShowSelectPageSize={isShowSelectPageSize}
              totalItems={totalItems}
              headerGroups={headerGroups}
            />
          </div>
        </div>
      )}
      <LoaderSpinner loading={loading} minHeight="600px" top="300px" />
    </>
  );
};

interface TableCardProps {
  data: {
    column: {
      isGrouped?: boolean;
      render: (type: string) => React.ReactNode;
    };
    render: (type: string) => React.ReactNode;
  }[];
  decoratorColor?: string;
}

const TableCard = ({ data, decoratorColor }: TableCardProps) => {
  
  return (
    <Col xs="12" style={{ marginBottom: 12 }}>
      <Card>
        <Card.Body
          className="p-0"
          style={{ position: "relative", overflowX: "auto" }}
        >
          <Table
            striped
            borderless
            className="mb-0"
            style={{ borderRadius: 5 }}
          >
            <Row decoratorColor={decoratorColor}>
              {data.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ width: "40%", minWidth: "100px" }}>
                    {item.column.isGrouped && (
                      <span style={{ fontWeight: "normal" }}>
                        {translate(LanguageKey.group_by_key)}:{" "}
                      </span>
                    )}
                    <label>{item.column.render("Header")}</label>
                  </td>
                  <td>{item.render("Cell")}</td>
                </tr>
              ))}
            </Row>
          </Table>
        </Card.Body>
      </Card>
    </Col>
  );
};

interface SntTablePagingProps {
  gotoPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageIndex: number;
  pageOptions: unknown[];
  pageSize: number;
  setPageSize: (size: number) => void;
  onConfigColumn?: () => void;
  isShowSelectPageSize: boolean;
  totalItems: { countByFilter: number };
  headerGroups: Record<string, unknown>[];
  toggleSortBy: (columnId: string, descending?: boolean) => void;
  isDisabbledGroup: boolean;
  dataFilter?: Record<string, unknown>;
}

const SntTablePaging = ({
  gotoPage,
  previousPage,
  nextPage,
  pageCount,
  canPreviousPage,
  canNextPage,
  pageIndex,
  pageOptions,
  pageSize,
  setPageSize,
  onConfigColumn,
  isShowSelectPageSize,
  totalItems,
  headerGroups,
  toggleSortBy,
  isDisabbledGroup,
  dataFilter,
}: SntTablePagingProps) => {
  
  const [showMobileSorting, setShowMobileSorting] = useState(false);
  const visibleTotal = getTotalItemView(totalItems, pageSize, pageIndex);

  return (
    <>
      <div>
        <div className="d-block d-md-inline-block float-md-start pt-2 mb-2">
          {totalItems && totalItems.countByFilter > 0 ? (
            <span>
              {translate(LanguageKey.showing_key)}{" "}
              <strong>
                {visibleTotal.from} - {visibleTotal.to}
                {"/"}
                {totalItems.countByFilter} {translate(LanguageKey.items_key)}
              </strong>{" "}
            </span>
          ) : (
            <span>
              {translate(LanguageKey.page_key)}{" "}
              <strong>
                {pageOptions.length === 0 ? 0 : pageIndex + 1} of{" "}
                {pageOptions.length}
              </strong>{" "}
            </span>
          )}
        </div>

        <div className="d-block d-md-inline-block float-md-end mb-2">
          <>
            <MobileSortingDiaglog
              headers={headerGroups[0].headers as any}
              show={showMobileSorting}
              setShowMobileSorting={setShowMobileSorting}
              toggleSortBy={toggleSortBy}
              isDisabbledGroup={isDisabbledGroup}
              dataFilter={dataFilter as any}
            />
            <div className="d-inline-block">
              <SntActionButton
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileSorting(true);
                }}
                style={{ color: "#0071a1" }}
              >
                <SntSortIcon />
              </SntActionButton>

              {onConfigColumn && (
                <SntActionButton
                  onClick={(e) => {
                    e.preventDefault();
                    onConfigColumn();
                  }}
                >
                  <IcomoonIcon
                    icon="cog"
                    color="#0071a1"
                    size="16"
                  ></IcomoonIcon>
                </SntActionButton>
              )}
            </div>
          </>

          {isShowSelectPageSize && (
            <div className="d-inline-block">
              <Form.Select
                value={pageSize}
                className="me-3"
                style={{
                  width: "115px",
                }}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[25, 50, 100, 200].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </Form.Select>
            </div>
          )}

          <div className="d-inline-block float-end">
            <Pagination className="mb-0">
              <Pagination.First
                disabled={!canPreviousPage}
                onClick={() => canPreviousPage && gotoPage(0)}
              />
              <Pagination.Prev
                disabled={!canPreviousPage}
                onClick={() => canPreviousPage && previousPage()}
              />
              <Pagination.Item
                active={true}
                onClick={() => gotoPage(pageIndex)}
              >
                {pageIndex + 1}
              </Pagination.Item>
              <Pagination.Next
                disabled={!canNextPage}
                onClick={() => canNextPage && nextPage()}
              />
              <Pagination.Last
                disabled={!canNextPage}
                onClick={() => canNextPage && gotoPage(pageCount - 1)}
              />
            </Pagination>
          </div>
        </div>
      </div>
    </>
  );
};

interface NoDataProps {
  noDataText?: string;
}

const NoData = ({ noDataText }: NoDataProps) => {
  return (
    <div
      className={"tr odd"}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="td">
        <NoDataContainer noDataText={noDataText} />
      </div>
    </div>
  );
};

const getTotalItemView = (totalItems: { countByFilter: number }, pageSize: number, pageIndex: number) => {
  pageIndex++;
  if (pageIndex * pageSize - pageSize > totalItems.countByFilter) {
    pageIndex = 1;
  }
  const from =
    pageIndex * pageSize - pageSize + 1 > totalItems.countByFilter
      ? 0
      : pageIndex * pageSize - pageSize + 1;
  const to =
    pageIndex * pageSize < totalItems.countByFilter
      ? pageIndex * pageSize
      : totalItems.countByFilter;

  return { from, to };
};
